import React, { useEffect, useState } from "react";
import { X, Eye, Check } from "lucide-react";
import { api, money, formatApiError } from "../../lib/api";
import { toast } from "sonner";

const CATEGORY_OPTIONS = [
  { value: "side-mirror", label: "Side Mirror" },
  { value: "windshield", label: "Windshield" },
  { value: "backglass", label: "Backglass" },
];
const POSITION_OPTIONS = [
  "left", "right", "pair", "front", "rear",
  "upper-left", "upper-right", "lower-left", "lower-right", "n/a",
];
const FEATURES = [
  { k: "heated", l: "Heated" },
  { k: "blind_spot", l: "Blind spot" },
  { k: "auto_dimming", l: "Auto-dimming" },
  { k: "with_camera", l: "With camera" },
  { k: "hud", l: "HUD" },
  { k: "rain_sensor", l: "Rain sensor" },
  { k: "with_backing_plate", l: "w/BackingPlate" },
];

const sel = "w-full h-9 px-2 text-sm border border-zinc-300 rounded-sm bg-white focus:border-zinc-900 focus:outline-none";

/**
 * Bulk-edit modal: preview (dry-run) -> confirm -> apply.
 * Never deletes; only sets the fields the admin explicitly changes.
 */
export default function BulkEditModal({ productIds, onClose, onApplied }) {
  const [category, setCategory] = useState("");
  const [position, setPosition] = useState("");
  const [fitmentType, setFitmentType] = useState("");
  const [priceMode, setPriceMode] = useState("");
  const [priceValue, setPriceValue] = useState("");
  const [invMode, setInvMode] = useState("");
  const [invValue, setInvValue] = useState("");
  const [featState, setFeatState] = useState({}); // k -> "on" | "off"
  const [status, setStatus] = useState(""); // "active" | "archived"

  // Shipping ("" / undefined = leave unchanged)
  const [shipClass, setShipClass] = useState("");
  const [weight, setWeight] = useState("");
  const [lengthIn, setLengthIn] = useState("");
  const [widthIn, setWidthIn] = useState("");
  const [heightIn, setHeightIn] = useState("");
  const [oversized, setOversized] = useState("");
  const [freight, setFreight] = useState("");
  const [shipsFree, setShipsFree] = useState("");
  const [classes, setClasses] = useState([]);

  useEffect(() => {
    api.get("/admin/shipping-classes").then(({ data }) => setClasses(data?.classes || (Array.isArray(data) ? data : []))).catch(() => setClasses([]));
  }, []);
  const classMap = React.useMemo(() => Object.fromEntries((classes || []).map((c) => [c.id, c.name])), [classes]);

  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const buildChanges = () => {
    const ch = {};
    if (category) ch.category = category;
    if (position) ch.position = position;
    if (fitmentType) ch.fitment_type = fitmentType;
    if (priceMode && priceValue !== "") { ch.price_mode = priceMode; ch.price_value = parseFloat(priceValue); }
    if (invMode && invValue !== "") { ch.inventory_mode = invMode; ch.inventory_value = parseInt(invValue, 10); }
    const feat = {};
    FEATURES.forEach((f) => {
      if (featState[f.k] === "on") feat[f.k] = true;
      else if (featState[f.k] === "off") feat[f.k] = false;
    });
    if (Object.keys(feat).length) ch.features = feat;
    if (status) ch.active = status === "active";
    // Shipping
    if (shipClass) ch.shipping_class_id = shipClass;
    if (weight !== "") ch.weight_lbs = parseFloat(weight);
    if (lengthIn !== "") ch.length_in = parseFloat(lengthIn);
    if (widthIn !== "") ch.width_in = parseFloat(widthIn);
    if (heightIn !== "") ch.height_in = parseFloat(heightIn);
    if (oversized) ch.oversized = oversized === "yes";
    if (freight) ch.freight_required = freight === "yes";
    if (shipsFree) ch.ships_free = shipsFree === "yes";
    return ch;
  };

  const changes = buildChanges();
  const hasChanges = Object.keys(changes).length > 0;

  const runPreview = async () => {
    if (!hasChanges) { toast.error("Choose at least one field to change"); return; }
    for (const [lbl, val] of [["Weight", weight], ["Length", lengthIn], ["Width", widthIn], ["Height", heightIn]]) {
      if (val !== "" && (!(parseFloat(val) > 0))) { toast.error(`${lbl} must be a positive number`); return; }
    }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/products/bulk-update", {
        product_ids: productIds, changes, dry_run: true,
      });
      setPreview(data);
      if (data.matched === 0) toast.info("No products would change with these settings");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const applyChanges = async () => {
    if (!preview || preview.matched === 0) return;
    if (!window.confirm(`Apply changes to ${preview.matched} product${preview.matched === 1 ? "" : "s"}? This cannot be auto-undone.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post("/admin/products/bulk-update", {
        product_ids: productIds, changes, dry_run: false,
      });
      toast.success(`Updated ${data.applied} product${data.applied === 1 ? "" : "s"}`);
      onApplied();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const fmt = (k, v) => {
    if (v === undefined || v === null) return "—";
    if (k === "price") return money(v);
    if (k === "active") return v ? "Active" : "Archived";
    if (k === "shipping_class_id") return classMap[v] || (v ? v : "none");
    if (["oversized", "freight_required", "ships_free"].includes(k)) return v ? "Yes" : "No";
    if (k === "features") return Object.entries(v).filter(([, on]) => on).map(([fk]) => fk).join(", ") || "none";
    return String(v);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto" data-testid="bulk-edit-modal">
      <div className="bg-white rounded-md w-full max-w-3xl my-8 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-200">
          <div>
            <h2 className="font-display text-lg">Bulk edit</h2>
            <p className="text-xs text-zinc-500">{productIds.length} product{productIds.length === 1 ? "" : "s"} selected · only the fields you change are updated</p>
          </div>
          <button onClick={onClose} data-testid="bulk-edit-close" className="p-2 hover:bg-zinc-100 rounded"><X size={18} /></button>
        </div>

        <div className="px-6 py-5 grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label="Category">
            <select data-testid="bulk-category" value={category} onChange={(e) => setCategory(e.target.value)} className={sel}>
              <option value="">— leave unchanged —</option>
              {CATEGORY_OPTIONS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </Field>
          <Field label="Position">
            <select data-testid="bulk-position" value={position} onChange={(e) => setPosition(e.target.value)} className={sel}>
              <option value="">— leave unchanged —</option>
              {POSITION_OPTIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </select>
          </Field>
          <Field label="Fitment type">
            <select data-testid="bulk-fitment-type" value={fitmentType} onChange={(e) => setFitmentType(e.target.value)} className={sel}>
              <option value="">— leave unchanged —</option>
              <option value="oem-style">OEM-style</option>
              <option value="aftermarket">Aftermarket</option>
            </select>
          </Field>
          <Field label="Status">
            <select data-testid="bulk-status" value={status} onChange={(e) => setStatus(e.target.value)} className={sel}>
              <option value="">— leave unchanged —</option>
              <option value="active">Active</option>
              <option value="archived">Archived (hidden)</option>
            </select>
          </Field>
          <Field label="Price">
            <div className="flex gap-2">
              <select data-testid="bulk-price-mode" value={priceMode} onChange={(e) => setPriceMode(e.target.value)} className={sel}>
                <option value="">— leave unchanged —</option>
                <option value="set">Set to</option>
                <option value="increase_pct">Increase %</option>
                <option value="decrease_pct">Decrease %</option>
                <option value="increase_amt">Increase $</option>
                <option value="decrease_amt">Decrease $</option>
              </select>
              {priceMode && (
                <input data-testid="bulk-price-value" type="number" step="0.01" min="0" value={priceValue} onChange={(e) => setPriceValue(e.target.value)} className={sel + " w-28"} placeholder="0.00" />
              )}
            </div>
          </Field>
          <Field label="Inventory">
            <div className="flex gap-2">
              <select data-testid="bulk-inv-mode" value={invMode} onChange={(e) => setInvMode(e.target.value)} className={sel}>
                <option value="">— leave unchanged —</option>
                <option value="set">Set to</option>
                <option value="increase">Increase by</option>
                <option value="decrease">Decrease by</option>
              </select>
              {invMode && (
                <input data-testid="bulk-inv-value" type="number" min="0" value={invValue} onChange={(e) => setInvValue(e.target.value)} className={sel + " w-28"} placeholder="0" />
              )}
            </div>
          </Field>
          <div className="md:col-span-2">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">Features</div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {FEATURES.map((f) => (
                <div key={f.k} className="flex items-center justify-between gap-2 text-sm">
                  <span className="text-zinc-700">{f.l}</span>
                  <select
                    data-testid={`bulk-feat-${f.k}`}
                    value={featState[f.k] || ""}
                    onChange={(e) => setFeatState((s) => ({ ...s, [f.k]: e.target.value }))}
                    className="h-8 px-2 text-xs border border-zinc-300 rounded-sm bg-white"
                  >
                    <option value="">—</option>
                    <option value="on">On</option>
                    <option value="off">Off</option>
                  </select>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 pb-1">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-3 border-t border-zinc-200 pt-4">Shipping</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Shipping class">
              <select data-testid="bulk-shipping-class" value={shipClass} onChange={(e) => setShipClass(e.target.value)} className={sel}>
                <option value="">— leave unchanged —</option>
                {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </Field>
            <Field label="Weight (lbs)">
              <input data-testid="bulk-weight" type="number" step="0.01" min="0" value={weight} onChange={(e) => setWeight(e.target.value)} className={sel} placeholder="leave unchanged" />
            </Field>
            <Field label="Dimensions (in) — L × W × H">
              <div className="flex gap-2">
                <input data-testid="bulk-length" type="number" step="0.01" min="0" value={lengthIn} onChange={(e) => setLengthIn(e.target.value)} className={sel} placeholder="L" />
                <input data-testid="bulk-width" type="number" step="0.01" min="0" value={widthIn} onChange={(e) => setWidthIn(e.target.value)} className={sel} placeholder="W" />
                <input data-testid="bulk-height" type="number" step="0.01" min="0" value={heightIn} onChange={(e) => setHeightIn(e.target.value)} className={sel} placeholder="H" />
              </div>
            </Field>
            <div className="grid grid-cols-3 gap-2">
              <Field label="Oversized">
                <select data-testid="bulk-oversized" value={oversized} onChange={(e) => setOversized(e.target.value)} className={sel}>
                  <option value="">—</option><option value="yes">Yes</option><option value="no">No</option>
                </select>
              </Field>
              <Field label="Freight">
                <select data-testid="bulk-freight" value={freight} onChange={(e) => setFreight(e.target.value)} className={sel}>
                  <option value="">—</option><option value="yes">Yes</option><option value="no">No</option>
                </select>
              </Field>
              <Field label="Ships free">
                <select data-testid="bulk-ships-free" value={shipsFree} onChange={(e) => setShipsFree(e.target.value)} className={sel}>
                  <option value="">—</option><option value="yes">Yes</option><option value="no">No</option>
                </select>
              </Field>
            </div>
          </div>
        </div>

        {preview && (
          <div className="px-6 pb-5" data-testid="bulk-edit-preview">
            <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-2">
              Preview — {preview.matched} product{preview.matched === 1 ? "" : "s"} will change
            </div>
            <div className="border border-zinc-200 rounded-md max-h-64 overflow-y-auto">
              {preview.results.length === 0 ? (
                <div className="px-4 py-6 text-sm text-zinc-500 text-center">No products would change.</div>
              ) : (
                <table className="w-full text-xs">
                  <thead className="bg-zinc-50 text-zinc-500 sticky top-0">
                    <tr><th className="text-left px-3 py-2">Product</th><th className="text-left px-3 py-2">Field</th><th className="text-left px-3 py-2">Before → After</th></tr>
                  </thead>
                  <tbody>
                    {preview.results.map((r) => (
                      Object.keys(r.after).map((k, i) => (
                        <tr key={r.id + k} className="border-t border-zinc-100">
                          {i === 0 ? <td rowSpan={Object.keys(r.after).length} className="px-3 py-2 align-top font-medium">{r.name}<div className="font-mono text-zinc-400">{r.sku}</div></td> : null}
                          <td className="px-3 py-2 text-zinc-600">{k}</td>
                          <td className="px-3 py-2 font-mono"><span className="text-zinc-400">{fmt(k, r.before[k])}</span> → <span className="text-zinc-900">{fmt(k, r.after[k])}</span></td>
                        </tr>
                      ))
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-zinc-200">
          <button onClick={onClose} className="px-4 h-10 text-sm text-zinc-600 hover:bg-zinc-100 rounded-sm">Cancel</button>
          <button
            onClick={runPreview}
            disabled={!hasChanges || busy}
            data-testid="bulk-edit-preview-btn"
            className="inline-flex items-center gap-2 px-4 h-10 text-sm border border-zinc-300 rounded-sm hover:bg-zinc-50 disabled:opacity-50"
          >
            <Eye size={14} /> Preview changes
          </button>
          <button
            onClick={applyChanges}
            disabled={!preview || preview.matched === 0 || busy}
            data-testid="bulk-edit-apply-btn"
            className="inline-flex items-center gap-2 px-4 h-10 text-sm bg-red-600 hover:bg-red-700 text-white rounded-sm disabled:opacity-50"
          >
            <Check size={14} /> {preview ? `Apply to ${preview.matched}` : "Apply"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1.5 block">{label}</label>
      {children}
    </div>
  );
}
